import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import HubIcon from '@mui/icons-material/Hub';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import WebIcon from '@mui/icons-material/Web';
import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import cmsNetworkLarge from '../assets/images/cms-network-1536.webp';
import cmsNetworkSmall from '../assets/images/cms-network-768.webp';
import BookCallLink from '../components/BookCallLink';
import Eyebrow from '../components/Eyebrow';
import MonoChips from '../components/stitch/MonoChips';
import { cardSx } from '../components/stitch/Section';
import TestimonialQuote from '../components/TestimonialQuote';
import { testimonials } from '../data/Testimonials';
import { tokens } from '../theme';

const capabilities = [
  {
    title: 'Content Platforms & CMS',
    icon: <ArticleIcon sx={{ fontSize: 28, color: tokens.accentText }} />,
    description:
      'Our principal has built publishing tools for distinct sites, languages, and editorial teams.',
    tech: ['Multi-site CMS', 'Editorial Roles', 'Multilingual Content'],
    bullets: [
      'Shared platform, distinct site identities',
      'Pages, posts, media, and event content',
      'Publishing tools for nontechnical staff',
    ],
  },
  {
    title: 'Custom Web Applications',
    icon: <WebIcon sx={{ fontSize: 28, color: tokens.accentText }} />,
    description:
      'Portals and internal tools that make everyday work easier for staff and customers.',
    tech: ['React', 'TypeScript', 'Next.js', '.NET', 'APIs'],
    bullets: [
      'Administrative portals and approval workflows',
      'Clear, accessible interfaces',
      'Inquiry and availability workflows',
    ],
  },
  {
    title: 'Deployment & Operations',
    icon: <CloudQueueIcon sx={{ fontSize: 28, color: tokens.accentText }} />,
    description:
      'Build, deployment, and hosting plans that make software easier to release and maintain.',
    tech: ['Docker', 'Cloudflare Workers', 'Vercel', 'CI/CD'],
    bullets: [
      'Repeatable builds and deployments',
      'Edge hosting that answers close to each visitor',
      'Clear handoffs for ongoing support',
    ],
  },
  {
    title: 'Data & Integrations',
    icon: <StorageIcon sx={{ fontSize: 28, color: tokens.accentText }} />,
    description: 'Connect applications to existing data, services, and the people who need them.',
    tech: ['PostgreSQL', 'MySQL', 'SQL Server', 'XML', 'REST APIs'],
    bullets: [
      'Data models built around real workflows',
      'APIs and external data feeds',
      'Search, reporting, and content management',
    ],
  },
];

// Built to the Stitch "Home" screen (project 1587770953174176333), with the site's own copy.
// Stitch's figures (22+ years, 194k+ units, 100% SLA) are replaced by facts the site states
// elsewhere: the predecessor practice dates from 2004 (About), the practice is principal-led,
// and the practice is US based, working remotely with teams around the world (footer, FAQ).
const facts = [
  {
    label: 'Experience',
    value: 'Since 2004',
    text: 'Lessons from Expert Web Coder, our predecessor software practice.',
  },
  {
    label: 'Who you work with',
    value: 'Principal-led',
    text: 'The person responsible for technical direction.',
  },
  {
    label: 'Where we work',
    value: 'US based',
    text: 'Working remotely with teams around the world.',
  },
];

const principles = [
  {
    label: 'Work With the Principal',
    title: 'Direct Collaboration',
    text: 'Work with the person responsible for the technical direction.',
  },
  {
    label: 'Fit the Workflow',
    title: 'Useful Connections',
    text: 'Bring applications, content, and data into one working process.',
  },
  {
    label: 'Plan for What Comes Next',
    title: 'Maintainable Systems',
    text: 'Make design and delivery choices your team can support over time.',
  },
];

const sectionGap = { mt: { xs: 8, md: 12 } } as const;

function SectionLabel({ children, onNavy = false }: { children: string; onNavy?: boolean }) {
  return (
    <Typography
      variant='labelCaps'
      component='p'
      sx={{ color: onNavy ? tokens.cyan : tokens.accentText }}
    >
      {children}
    </Typography>
  );
}

const arrowLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  color: tokens.accentText,
  fontWeight: 600,
  fontSize: '0.875rem',
  textUnderlineOffset: '0.25em',
} as const;

const Home: React.FC = () => {
  return (
    <Container sx={{ pt: { xs: 4, md: 8 } }}>
      {/* Hero */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
          gap: { xs: 5, lg: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Eyebrow>Content Platforms &amp; Custom Software</Eyebrow>
          <Typography
            variant='h1'
            component='h1'
            sx={{ mt: 2.5 }}
          >
            Make content easier to publish, approve, and maintain.
          </Typography>
          <Typography
            variant='bodyLg'
            component='p'
            sx={{ mt: 3, maxWidth: '38rem' }}
          >
            Scaled Tech builds content platforms and custom applications around your team&apos;s
            workflow. We help connect editors, approval processes, and existing systems, with direct
            access to the principal responsible for technical direction.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 4 }}>
            <Button
              component={Link}
              to='/case-studies'
              variant='contained'
              size='large'
              endIcon={<HubIcon aria-hidden='true' />}
              sx={{ px: 3, py: 1.5 }}
            >
              Read the case studies
            </Button>
            <Button
              component={Link}
              to='/contact-us'
              data-track='discuss_project'
              variant='outlined'
              size='large'
              startIcon={<ArrowForwardIcon aria-hidden='true' />}
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: tokens.surfaceLowest,
                borderColor: tokens.outlineVariant,
                color: tokens.onSurface,
                '&:hover': { bgcolor: tokens.surfaceLow, borderColor: tokens.outline },
              }}
            >
              Discuss your project
            </Button>
          </Box>

          <Box
            component='ul'
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 4,
              p: 0,
              pt: 2.5,
              borderTop: `1px solid ${tokens.outlineVariant}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
            }}
          >
            {[
              { icon: CheckCircleIcon, text: 'Custom Software' },
              { icon: SecurityIcon, text: 'Connected Systems' },
              { icon: CheckCircleIcon, text: 'Principal-Led Work' },
            ].map(({ icon: Icon, text }) => (
              <Box
                component='li'
                key={text}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Icon
                  aria-hidden='true'
                  sx={{ fontSize: 18, color: tokens.accentText }}
                />
                <Typography
                  variant='bodySm'
                  sx={{ color: tokens.onSurfaceVariant, fontWeight: 600 }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Original concept illustration; not a client interface screenshot. */}
        <Box
          component='figure'
          sx={{
            ...cardSx,
            m: 0,
            overflow: 'hidden',
            boxShadow: '0 24px 48px -16px rgba(0, 16, 38, 0.22)',
          }}
        >
          {/* React 18 forwards the lowercase attribute without an unknown-prop warning. */}
          <img
            {...{ fetchpriority: 'high' }}
            sizes='(max-width: 600px) calc(100vw - 32px), (max-width: 1200px) calc(100vw - 48px), 500px'
            srcSet={`${cmsNetworkSmall} 768w, ${cmsNetworkLarge} 1536w`}
            src={cmsNetworkLarge}
            width={1536}
            height={1024}
            loading='eager'
            decoding='async'
            alt='Concept illustration of one content hub serving four distinct websites.'
            style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
          />
          <Typography
            component='figcaption'
            variant='bodySm'
            sx={{ display: 'block', px: 2, py: 1.5, color: tokens.onSurfaceVariant }}
          >
            Shared publishing, distinct site identities
          </Typography>
        </Box>
      </Box>

      {/* Navy facts band (Stitch metrics band, with facts the site can stand behind) */}
      <Box
        component='ul'
        aria-label='About the practice'
        sx={{
          ...sectionGap,
          listStyle: 'none',
          m: 0,
          p: { xs: 3, md: 4 },
          bgcolor: tokens.navy,
          borderRadius: 3,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
          gap: { xs: 3, md: 0 },
        }}
      >
        {facts.map((fact, index) => (
          <Box
            component='li'
            key={fact.label}
            sx={{
              px: { md: 3 },
              borderLeft: { md: index ? '1px solid rgba(255, 255, 255, 0.14)' : 0 },
            }}
          >
            <SectionLabel onNavy>{fact.label}</SectionLabel>
            <Typography
              component='p'
              variant='h3'
              sx={{ color: '#FFFFFF', mt: 1 }}
            >
              {fact.value}
            </Typography>
            <Typography
              variant='bodySm'
              component='p'
              sx={{ color: tokens.onNavyMuted, mt: 0.5 }}
            >
              {fact.text}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Delivered examples before the capability inventory. */}
      <Box
        component='section'
        aria-labelledby='selected-work-heading'
        sx={{ ...sectionGap, bgcolor: tokens.surfaceLow, borderRadius: 3, p: { xs: 3, md: 5 } }}
      >
        <Box sx={{ textAlign: { md: 'center' }, maxWidth: '44rem', mx: { md: 'auto' } }}>
          <SectionLabel>Selected work</SectionLabel>
          <Typography
            variant='h2'
            component='h2'
            id='selected-work-heading'
            sx={{ mt: 1 }}
          >
            Publishing tools in practice
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {[
            {
              eyebrow: 'Delivered through our predecessor software practice',
              title: 'One CMS, distinct sites',
              text: 'Our principal built a shared CMS that let staff manage separately branded sites with their own content, navigation, and editorial access, including publishing in English, Spanish, and French.',
            },
            {
              eyebrow: 'Delivered by Scaled Tech for The Garden Marquee',
              title: 'Content and inquiries together',
              text: 'Scaled Tech built staff editing tools for venue pages, posts, galleries, and FAQs alongside an inquiry flow that checks date availability and brings requests into an administrative workflow.',
            },
          ].map((study) => (
            <Box
              component='article'
              key={study.title}
              sx={{ ...cardSx, p: { xs: 3, md: 4 } }}
            >
              <Typography
                variant='labelCaps'
                component='p'
                sx={{
                  display: 'inline-block',
                  color: tokens.accentText,
                  bgcolor: tokens.primaryFixed,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {study.eyebrow}
              </Typography>
              <Typography
                component='h3'
                variant='h4'
                sx={{ mt: 2 }}
              >
                {study.title}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 1 }}
              >
                {study.text}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
          <Box
            component={Link}
            to='/case-studies'
            sx={arrowLinkSx}
          >
            Explore all CMS case studies
            <ArrowForwardIcon
              aria-hidden='true'
              sx={{ fontSize: 16 }}
            />
          </Box>
          <Box
            component='a'
            href='/cms-capabilities'
            data-track='capability_statement'
            sx={arrowLinkSx}
          >
            View CMS capability statement
            <ArrowForwardIcon
              aria-hidden='true'
              sx={{ fontSize: 16 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Client references: approved excerpts from signed reference letters. */}
      <Box
        component='section'
        aria-labelledby='client-words-heading'
        sx={sectionGap}
      >
        <SectionLabel>Client references</SectionLabel>
        <Typography
          variant='h2'
          component='h2'
          id='client-words-heading'
          sx={{ mt: 1, mb: 4 }}
        >
          In our clients’ words
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {testimonials.map((testimonial) => (
            <TestimonialQuote
              key={testimonial.id}
              testimonial={testimonial}
            />
          ))}
        </Box>
      </Box>

      {/* Core capabilities */}
      <Box
        component='section'
        aria-labelledby='capabilities-heading'
        sx={sectionGap}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            gap: 2,
          }}
        >
          <Box sx={{ maxWidth: '44rem' }}>
            <SectionLabel>Core capabilities</SectionLabel>
            <Typography
              variant='h2'
              component='h2'
              id='capabilities-heading'
              sx={{ mt: 1 }}
            >
              From the Interface to the Systems Behind It
            </Typography>
          </Box>
          <Box
            component={Link}
            to='/services'
            sx={arrowLinkSx}
          >
            Explore Services
            <ArrowForwardIcon
              aria-hidden='true'
              sx={{ fontSize: 16 }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {capabilities.map((cap) => (
            <Box
              key={cap.title}
              sx={{ ...cardSx, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column' }}
            >
              <Box
                aria-hidden='true'
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: tokens.surfaceContainer,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {cap.icon}
              </Box>
              <Typography
                variant='h4'
                component='h3'
                sx={{ mt: 2.5 }}
              >
                {cap.title}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 1, mb: 2.5 }}
              >
                {cap.description}
              </Typography>
              <MonoChips items={cap.tech} />
              <Box
                component='ul'
                sx={{
                  listStyle: 'none',
                  m: 0,
                  mt: 3,
                  p: 0,
                  pt: 2.5,
                  borderTop: `1px solid ${tokens.outlineVariant}`,
                  display: 'grid',
                  gap: 1.25,
                }}
              >
                {cap.bullets.map((bullet) => (
                  <Box
                    component='li'
                    key={bullet}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                  >
                    <CheckCircleIcon
                      aria-hidden='true'
                      sx={{ fontSize: 16, color: tokens.accentText }}
                    />
                    <Typography
                      variant='bodySm'
                      sx={{ color: tokens.onSurface }}
                    >
                      {bullet}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Working principles (Stitch value pillars) */}
      <Box
        component='section'
        aria-labelledby='principles-heading'
        sx={sectionGap}
      >
        <Box sx={{ textAlign: { md: 'center' }, maxWidth: '44rem', mx: { md: 'auto' } }}>
          <SectionLabel>How we work with you</SectionLabel>
          <Typography
            variant='h2'
            component='h2'
            id='principles-heading'
            sx={{ mt: 1 }}
          >
            Working principles
          </Typography>
        </Box>
        <Box
          component='ul'
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 4,
            p: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {principles.map((principle, index) => (
            <Box
              component='li'
              key={principle.title}
              sx={{ ...cardSx, p: 3, bgcolor: tokens.surfaceLow }}
            >
              <Box
                aria-hidden='true'
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: tokens.navy,
                  color: tokens.cyan,
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: '"JetBrains Mono Variable", ui-monospace, monospace',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                }}
              >
                {String(index + 1).padStart(2, '0')}
              </Box>
              <Box sx={{ mt: 2 }}>
                <SectionLabel>{principle.label}</SectionLabel>
              </Box>
              <Typography
                component='h3'
                variant='h4'
                sx={{ mt: 0.5 }}
              >
                {principle.title}
              </Typography>
              <Typography
                variant='bodySm'
                component='p'
                sx={{ mt: 1, color: tokens.onSurfaceVariant }}
              >
                {principle.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Defined first engagement (Stitch assessment band) */}
      <Box
        component='section'
        aria-labelledby='first-engagement-heading'
        sx={{
          ...sectionGap,
          bgcolor: tokens.navy,
          color: '#FFFFFF',
          borderRadius: 3,
          p: { xs: 3, md: 6 },
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
          gap: { xs: 4, lg: 6 },
          alignItems: 'center',
          backgroundImage:
            'radial-gradient(36rem 18rem at 90% 10%, rgba(76, 214, 251, 0.10), transparent 70%)',
          '& a:focus-visible, & .Mui-focusVisible': {
            outline: '3px solid #FFFFFF !important',
            outlineOffset: '3px !important',
          },
        }}
      >
        <Box>
          <SectionLabel onNavy>Defined First Engagement</SectionLabel>
          <Typography
            variant='h2'
            component='h2'
            id='first-engagement-heading'
            sx={{ mt: 1, color: '#FFFFFF' }}
          >
            Start with a CMS &amp; Workflow Assessment
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 2, color: tokens.onNavyMuted, maxWidth: '40rem' }}
          >
            Whether you are improving an existing CMS or software platform or planning a new build
            or connected workflow, begin with a fixed-scope 2 to 3 week assessment. We quote a fixed
            fee after a short call based on the systems in scope.
          </Typography>
          <Box
            component='ul'
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 3,
              p: 0,
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            {[
              'Current-state workflow and editorial role map',
              'Architecture and data-flow diagram',
              'Prioritized recommendations for governance, accessibility, or integration gaps',
              'Written implementation roadmap and scoped proposal for next steps',
            ].map((item) => (
              <Box
                component='li'
                key={item}
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
              >
                <CheckCircleIcon
                  aria-hidden='true'
                  sx={{ fontSize: 18, color: tokens.cyan, mt: '2px' }}
                />
                <Typography
                  variant='bodySm'
                  component='span'
                  sx={{ color: '#FFFFFF' }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            bgcolor: tokens.surfaceLowest,
            color: tokens.onSurface,
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '& a:focus-visible, & .Mui-focusVisible': {
              outlineColor: `${tokens.focus} !important`,
            },
          }}
        >
          <Typography
            component='p'
            variant='h5'
          >
            Ready to start?
          </Typography>
          <Typography variant='bodySm'>
            Ask for an assessment, read the details, or book a short call first.
          </Typography>
          <Button
            component={Link}
            to='/contact-us?subject=cms-workflow-assessment'
            data-track='assessment_request'
            variant='contained'
            endIcon={<ArrowForwardIcon aria-hidden='true' />}
            sx={{ mt: 1 }}
          >
            Request a CMS &amp; workflow assessment
          </Button>
          <BookCallLink sx={{ justifyContent: 'center' }} />
          <Box
            component={Link}
            to='/services#first-engagement'
            sx={{ ...arrowLinkSx, alignSelf: 'center', mt: 0.5 }}
          >
            See assessment details on Services
            <ArrowForwardIcon
              aria-hidden='true'
              sx={{ fontSize: 16 }}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Home;
